<template>
  <FlashNotifications ref="flash" />

  <div class="relative overflow-hidden flex flex-col h-app-screen pb-14 md:pb-0 bg-gray-50 dark:bg-[#0d1117]">
    <!-- One header: title, totals, refresh, the settings gear, and the filters -->
    <header class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700 shadow-[0_1px_2px_rgba(16,24,40,0.04)] relative z-20">
      <div class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 sm:px-6 pt-2.5 pb-2">
        <h1 class="text-lg font-bold tracking-tight text-gray-900 dark:text-white leading-none">Job Listings</h1>

        <div class="ml-auto flex items-center gap-2">
          <HelpLink path="job-tracker/fetch-jobs" label="Guide" class="hidden sm:inline-flex" />
          <div v-if="jobs.loaded && jobs.counts.all > 0" class="hidden sm:flex items-center gap-2">
            <span class="inline-flex items-center gap-1.5 h-7 px-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"/></svg>
              <span><span class="font-bold text-gray-900 dark:text-white tabular-nums">{{ jobs.counts.all }}</span> saved</span>
            </span>
            <span class="inline-flex items-center gap-1.5 h-7 px-2 rounded-lg border text-xs"
              :class="jobs.counts.applied > 0
                ? 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-900/40 text-green-700 dark:text-green-400'
                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>
              <span><span class="font-bold tabular-nums">{{ jobs.counts.applied }}</span> applied</span>
            </span>
          </div>

          <button type="button" @click="refresh" :disabled="refreshing" title="Refresh"
            class="inline-flex items-center gap-1.5 h-7 px-2 sm:px-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] text-[13px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
            <svg class="w-3.5 h-3.5" :class="refreshing ? 'animate-spin' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"/>
            </svg>
            <span class="hidden sm:inline">{{ refreshing ? 'Refreshing...' : 'Refresh' }}</span>
            <span class="sr-only sm:hidden">Refresh</span>
          </button>

          <IconButton :icon="SETTINGS_ICON" label="Job settings" align="right" :tone="settingsOpen ? 'active' : 'default'" :pressed="settingsOpen" :hide-tip="settingsOpen" @click="openSettings()" />

          <!-- A job from anywhere else: typed in by hand on its own page -->
          <router-link to="/jobs/new" data-turbo-prefetch="false"
            class="inline-flex items-center gap-1.5 h-7 px-2.5 sm:px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-[13px] font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-1">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/></svg>
            <span class="hidden sm:inline">Add a job</span>
            <span class="sr-only sm:hidden">Add a job</span>
          </router-link>
        </div>
      </div>

      <div v-if="showFilters" class="px-4 sm:px-6 pb-2.5">
        <JobsFilters :filters="jobs.filters" :titles="searches.titles" :locations="searches.locations"
          :counts="jobs.counts" :active="jobs.isFiltered" @change="jobs.setFilters" @clear="jobs.clearFilters" />
      </div>
    </header>

    <!-- Content -->
    <div class="flex-1 min-h-0">
      <JobsBoard @go-searches="openSettings('titles')" @open-company="companyId = $event" @open-ai-settings="openSettings('ai')" />
    </div>

    <CompanyPanel :company-id="companyId" @close="companyId = null" />

    <JobsSettingsPanel :open="settingsOpen" :section="section" @close="settingsOpen = false" @select="section = $event" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useFetchedJobsStore } from 'stores/fetchedJobs.js'
import { useJobSearchStore } from 'stores/jobSearch.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import IconButton from 'controllers/vue_components/shared/IconButton.vue'
import JobsBoard from './JobsBoard.vue'
import JobsFilters from './JobsFilters.vue'
import JobsSettingsPanel from './JobsSettingsPanel.vue'
import CompanyPanel from './CompanyPanel.vue'
import HelpLink from 'controllers/vue_components/shared/HelpLink.vue'

// The gear (Heroicons cog-6-tooth, outline).
const SETTINGS_ICON = 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z'

const route = useRoute()
const router = useRouter()
const jobs = useFetchedJobsStore()
const searches = useJobSearchStore() // its titles and locations fill the filters, and it is what the settings panel edits
const flash = ref(null)
const refreshing = ref(false)
const settingsOpen = ref(false)
const companyId = ref(null) // the company open in the slide over panel
const section = ref('titles')

// The filters live in the header, so they show above the jobs, and only once there is
// something to filter (or a filter is on, so it can be cleared).
const showFilters = computed(() => jobs.loaded && (jobs.counts.all > 0 || jobs.isFiltered))

function openSettings(id = section.value) {
  section.value = id
  settingsOpen.value = true
}

// Reloads both the jobs and the searches at once, since a run in the extension changes both: new jobs, and
// each search's count and last fetched time. What is on screen stays while it loads.
async function refresh() {
  if (refreshing.value) return

  refreshing.value = true
  await Promise.all([jobs.refresh(), searches.fetchSettings({ silent: true })])
  refreshing.value = false

  const problem = jobs.error || searches.error
  if (problem) flash.value?.show(`Could not refresh. ${problem}`, 'alert')
}

// A link to a job (/jobs?job=ID, from "Made for ..." on a resume) opens that job.
watch(() => route.query.job, (id) => {
  if (!id) return
  jobs.openFromLink(String(id))
  router.replace({ path: '/jobs', query: {} })
}, { immediate: true })

onMounted(() => {
  searches.fetchIfStale()

  // The settings used to be a Searches tab (/jobs?tab=searches): an old link opens the panel instead.
  if (route.query.tab === 'searches') {
    openSettings('titles')
    router.replace({ path: '/jobs', query: {} })
  }
})
</script>
